import { defineStore } from 'pinia'
import { computed, ref, type Ref } from 'vue'
import { fetchTermsConfig, fetchTermsStatus } from '@/composables/useTerms'
import { fetchAvvStatus } from '@/composables/useAvv'
import { handleAxiosError } from '@/composables/useAxiosError'
import { useJwtStore } from './jwt'
import { useSubAccountsStore } from './subAccounts'
import type { AvvStatus, TermsConfig, TermsStatus } from '@/types'

// Callers share one request until it fails; force starts a new one. Only the latest request
// or set writes the value, and a failed forced load clears it, so the accept page cannot post
// the version of an older answer, nor lose the status its acceptance returned.
const sharedLoader = <T>(fetch: () => Promise<T | null>, target: Ref<T | null>, logPrefix: string) => {
  let request: Promise<void> | null = null
  let generation = 0
  const set = (value: T | null) => {
    generation++
    request = null
    target.value = value
  }
  const load = (force = false): Promise<void> => {
    if (request && !force) return request
    const current = ++generation
    request = fetch()
      .then((value) => {
        if (current === generation) target.value = value
      })
      .catch((err) => {
        if (current !== generation) return
        request = null
        if (force) target.value = null
        handleAxiosError(err, { logPrefix, reportNetworkErrors: false })
      })
    return request
  }
  return { load, set }
}

/** Status and published versions, shared by the banner, the accept page and checkout; the AVV standing alongside. */
export const useTermsStore = defineStore('terms', () => {
  const status = ref<TermsStatus | null>(null)
  const config = ref<TermsConfig | null>(null)
  const avvStatus = ref<AvvStatus | null>(null)

  const { load: loadStatus, set: setStatus } = sharedLoader(fetchTermsStatus, status, 'TermsStore:status')
  const { load: loadConfig } = sharedLoader(fetchTermsConfig, config, 'TermsStore:config')
  const { load: loadAvvStatus, set: setAvvStatus } = sharedLoader(fetchAvvStatus, avvStatus, 'TermsStore:avv')

  return { status, config, avvStatus, loadStatus, loadConfig, setStatus, loadAvvStatus, setAvvStatus }
})

/**
 * Whether this session may be offered the acceptance. The server decides with
 * can_accept; an impersonation or context switch is never offered it even if
 * an answer said otherwise, because an acceptance there would be the wrong
 * person's.
 */
export const useTermsAcceptance = () => {
  const store = useTermsStore()
  const jwtStore = useJwtStore()
  const subAccounts = useSubAccountsStore()
  const ownSession = computed(() => {
    const jwt = jwtStore.getJwtContent
    return (
      !!jwt &&
      jwt.is_impersonation !== true &&
      !jwt.impersonating_admin_id &&
      !jwt.original_account_id &&
      !subAccounts.isContextSwitched
    )
  })
  const mayAccept = computed(() => ownSession.value && store.status?.can_accept === true)
  const prompt = computed(() => mayAccept.value && store.status?.needs_acceptance === true)
  // The AVV acceptance has the same holder fence, so it is offered to the same sessions.
  const avvPrompt = computed(
    () => mayAccept.value && store.avvStatus?.required === true && store.avvStatus.needs_reaccept
  )
  return { ownSession, mayAccept, prompt, avvPrompt }
}
